import { FC, MouseEvent, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { Popover, Icon, Avatar, Heading, Text } from '@livechat/design-system-react-components';
import { Help as HelpIcon, ChatDots as ChatDotsIcon, Logout } from '@livechat/design-system-icons';

import { useOrganizations } from 'hooks/use-organizations';
import { FeatureNames, useFetchFeatures } from 'hooks/use-fetch-features';
import { useGetMe } from 'hooks/use-get-me';
import ChatWidget from 'services/ChatWidget';
import Amplitude from 'services/Amplitude';
import { sessionsClient } from 'services/connectivity/session/client';
import { getAccessToken, getAccountOrganizationProducts } from 'helpers/account';
import { removeItem } from 'helpers/storage/local-storage';

import { OrganizationMenu } from './organization-menu/OrganizationMenu';
import { OrganizationDetails } from './organization-details/OrganizationDetails';
import { goToProductStyles } from './organization-details/styles';
import { ProfileDropdownPopover } from './profile-dropdown-popover/ProfileDropdownPopover';

import {
  profileDropdownContainerStyles,
  popoverStyles,
  dropdownContainerStyles,
  userDetailsContainerStyles,
  userDetailsRightContainerStyles,
  accountEmailStyles,
  accountNameStyles,
  popoverLineStyles,
  linkStyles,
  bottomLinkContainerStyles,
  organizationNameContainerStyles,
  yourOrganizationStyles,
  organizationNameStyles,
} from './styles';

export const ProfileDropdown: FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { avatarUrl, email, name, roles } = useGetMe();
  const { organizations } = useOrganizations();
  const { data: features } = useFetchFeatures();

  const accessToken = getAccessToken();
  const textPlatformAsProductEnabled = features?.[FeatureNames.TextPlatformAsProduct];
  const filteredOrganizations = getAccountOrganizationProducts(organizations, roles).filter(
    ({ products }) => products.some(({ haveAccessToProduct }) => haveAccessToProduct)
  );
  const hasMultipleOrganizations = filteredOrganizations.length > 1;

  const handleOpen = () => setIsExpanded(true);
  const handleClose = () => setIsExpanded(false);

  const handleLogout = () => {
    if (accessToken) {
      sessionsClient.removeCurrent(accessToken);
    }

    removeItem('signup');
    Cookies.remove('access_token');
    Amplitude.logEvent(email, 'AVATAR_MENU_ITEM_CLICK', { menuElement: 'log out' }, true);
  };

  const handleHelpCenterClick = () => {
    handleClose();
    Amplitude.logEvent(email, 'AVATAR_MENU_ITEM_CLICK', { menuElement: 'help center' }, true);
  };

  const handleContactSupport = (e: MouseEvent) => {
    handleClose();
    ChatWidget.handleContactSupport(e);
    Amplitude.logEvent(email, 'AVATAR_MENU_ITEM_CLICK', {
      menuElement: 'chat',
    });
  };

  const profileItem = useMemo(
    () => (
      <div onClick={handleOpen}>
        <ProfileDropdownPopover
          avatarUrl={hasMultipleOrganizations ? null : avatarUrl}
          accountName={name || email}
          accountEmail={email}
        />
      </div>
    ),
    [hasMultipleOrganizations, name, email, avatarUrl]
  );

  return (
    <div css={profileDropdownContainerStyles}>
      <Popover
        placement="bottom-end"
        triggerRenderer={profileItem}
        css={popoverStyles}
        onClose={handleClose}
        isVisible={isExpanded}
      >
        <div css={dropdownContainerStyles} data-testid="profile-menu">
          <div css={userDetailsContainerStyles}>
            <Avatar
              // eslint-disable-next-line no-nested-ternary
              type={hasMultipleOrganizations ? 'text' : avatarUrl ? 'image' : 'text'}
              src={hasMultipleOrganizations ? null : avatarUrl}
              text={name || email}
            />
            <div css={userDetailsRightContainerStyles}>
              {name && (
                <Heading size="xs" css={accountNameStyles}>
                  {name}
                </Heading>
              )}
              {email && <Text css={accountEmailStyles}>{email}</Text>}
            </div>
          </div>
          {hasMultipleOrganizations ? (
            <div>
              {filteredOrganizations.map((organization) => (
                <OrganizationMenu
                  key={organization.id}
                  organization={organization}
                  textPlatformAsProductEnabled={textPlatformAsProductEnabled}
                />
              ))}
            </div>
          ) : (
            <div>
              {filteredOrganizations && filteredOrganizations.length === 1 && (
                <div>
                  <div css={organizationNameContainerStyles}>
                    {filteredOrganizations[0].name ? (
                      <div>
                        <Text size="xs" css={yourOrganizationStyles}>
                          Your organization:
                        </Text>
                        <Heading size="xs" css={organizationNameStyles}>
                          {filteredOrganizations[0].name}
                        </Heading>
                      </div>
                    ) : (
                      <div>
                        <Text size="xs" css={yourOrganizationStyles}>
                          Organization created on
                        </Text>
                        <Heading size="xs" css={organizationNameStyles}>
                          {filteredOrganizations[0].createdAt}
                        </Heading>
                      </div>
                    )}
                  </div>
                  <OrganizationDetails
                    organization={filteredOrganizations[0]}
                    textPlatformAsProductEnabled={textPlatformAsProductEnabled}
                  />
                </div>
              )}
            </div>
          )}
          <div css={popoverLineStyles} />
          <div>
            <a
              css={linkStyles}
              href="https://textaccounts.kb.help/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="help-center-link"
              onClick={handleHelpCenterClick}
            >
              <div css={bottomLinkContainerStyles}>
                <Icon source={HelpIcon} size="large" />
                <Heading size="xs" css={goToProductStyles}>
                  Help center
                </Heading>
              </div>
            </a>
            <div css={bottomLinkContainerStyles} onClick={handleContactSupport}>
              <Icon source={ChatDotsIcon} size="large" />
              <Heading size="xs" css={goToProductStyles}>
                Chat with us
              </Heading>
            </div>
            <a href="/" css={linkStyles} data-testid="logout-link">
              <div css={bottomLinkContainerStyles} onClick={handleLogout}>
                <Icon size="large" kind="action-negative" source={Logout} />
                <Heading size="xs" css={goToProductStyles}>
                  Log out
                </Heading>
              </div>
            </a>
          </div>
        </div>
      </Popover>
    </div>
  );
};
